import { useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { FaCheckCircle, FaDownload } from "react-icons/fa";
import { motion } from "framer-motion";
import React from "react";
import { dataConfig } from "./data.config";
import { useProductCounter } from "../../hooks/useProductCounter";
import { useServiceCounter } from "../../hooks/useServiceCounter";
import brochurePdf from "../../assets/doc_pdf/doc.pdf";

export default function DetailPage() {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace("#", "");
      const element: any = document.getElementById(sectionId);
      const headerOffset = 120; // pixels you want to keep above
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;
      if (element) {
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [location]);

  const [activeTab, setActiveTab] = useState("description");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prodServiceName = searchParams.get("key");
  const [data, setData] = useState<any | null>();
  const [selectedImg, setSelectedImg] = useState<string>("");
  const { incrementProductCountBySlug } = useProductCounter();
  const { incrementServiceCountBySlug } = useServiceCounter();

  React.useEffect(() => {
    if (!prodServiceName) {
      navigate("/home"); // No key → go home
      return;
    }

    let tempData = dataConfig.productsData.proLabGB2;

    switch (prodServiceName) {
      case "prolab-gb-2":
        tempData = dataConfig.productsData.proLabGB2;
        incrementProductCountBySlug("prolab-gb-2");
        break;
      case "prolab-gb-3":
        tempData = dataConfig.productsData.proLabGB3;
        incrementProductCountBySlug("prolab-gb-3");
        break;
      case "prolab-gb-4":
        tempData = dataConfig.productsData.proLabGB4;
        incrementProductCountBySlug("prolab-gb-4");
        break;
      case "prolab-gb-custom":
        tempData = dataConfig.productsData.proLabGBCustom;
        incrementProductCountBySlug("prolab-gb-custom");
        break;
      case "filter-purify":
        tempData = dataConfig.servicesData.filterPurification;
        incrementServiceCountBySlug("filter-purify");
        break;
      case "gi-pm":
        tempData = dataConfig.servicesData.gloveboxMaintan;
        incrementServiceCountBySlug("gi-pm");
        break;
      case "seal-gasket":
        tempData = dataConfig.servicesData.sealGasketReplace;
        incrementServiceCountBySlug("seal-gasket");
        break;
      case "sensor-clean":
        tempData = dataConfig.servicesData.sensorCleaning;
        incrementServiceCountBySlug("sensor-clean");
        break;
      case "techSupport":
        tempData = dataConfig.servicesData.techSupport;
        incrementServiceCountBySlug("techSupport");
        break;
      case "vacum-inert":
        tempData = dataConfig.servicesData.vaccumGasService;
        incrementServiceCountBySlug("vacum-inert");
        break;

      default:
        navigate("/"); // No matching ID → go home
        return;
    }

    setData(tempData ?? null);
  }, [prodServiceName, navigate]);

  React.useEffect(() => {
    if (data?.productsDetails?.imgList?.length) {
      setSelectedImg(data.productsDetails.imgList[0]);
    }
  }, [data]);

  if (!data) {
    return <div>Loading...</div>;
  }
  return (
    <div className="w-full bg-white text-gray-900">
      {/* ===================== HERO ====================== */}
      <section
        className="relative h-[200px] md:h-[300px] flex items-center justify-center bg-cover bg-center  mt-[80px]"
        style={{
          backgroundImage: `url(${data.hero.imgUrl})`,
        }}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/90 to-gray-900"></div>
        <div className="relative z-10 text-center px-4 max-w-3xl">
          <h1 className="text-white font-extrabold text-3xl md:text-4xl lg:text-5xl tracking mb-2">
            {data.hero.title}
            <span className="text-[#ff1a1a]">{data.hero.title2}</span>
          </h1>
          <div className="w-20 h-1 bg-[#ff1a1a] mx-auto rounded-full mb-4"></div>
          <p className="text-white text-base md:text-lg font-workSans font-medium">
            {data.hero.description}
          </p>
        </div>
      </section>

      {/* ===================== PRODUCT SECTION ====================== */}
      <section className="max-w-7xl mx-auto py-20 px-6 grid grid-cols-1 md:grid-cols-2 gap-16">
        {/* LEFT (40%) - Image Gallery */}
        <div className="flex flex-col items-center">
          <div className="w-full max-w-xl h-[420px] flex items-center justify-center overflow-hidden rounded-xl border border-gray-300 bg-white">
            {selectedImg && (
              <motion.img
                key={selectedImg}
                src={selectedImg}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="w-full h-full object-fill"
                alt="Product"
              />
            )}
          </div>

          {/* Thumbnails */}
          {data.productsDetails.imgList.length > 1 && (
            <div className="flex gap-4 mt-6 overflow-x-auto overflow-y-hidden scrollbar-hide w-full">
              {data.productsDetails.imgList.map((img: string, idx: number) => (
                <img
                  key={idx}
                  src={img}
                  onClick={() => setSelectedImg(img)}
                  className={`w-20 h-20 rounded-lg cursor-pointer border transition flex-shrink-0
        ${
          selectedImg === img
            ? "border-red-600 shadow-md scale-105"
            : "border-gray-300 hover:scale-105"
        }`}
                  alt="thumb"
                />
              ))}
            </div>
          )}
        </div>

        {/* RIGHT (60%) - Product Info */}
        <div>
          <h2 className="text-3xl font-bold mb-4 tracking-tight text-[#222]">
            {data.productsDetails.productName}
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            {data.productsDetails.description}
          </p>

          {/* Key Features */}
          <div className="space-y-3">
            {data.productsDetails.keyFeatures.map(
              (item: any, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <FaCheckCircle className="text-red-600 text-xl" />
                  <span className="text-gray-700">{item}</span>
                </motion.div>
              )
            )}
          </div>
          {/* Download Brochure */}
          <div className="mt-8">
            <a
              href={brochurePdf}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 rounded-lg
               bg-red-600 text-white font-semibold
               hover:bg-red-700 transition-all shadow-md"
            >
              <FaDownload className="text-lg" />
              {data.productsDetails.download?.title || "Download Brochure"}
            </a>
          </div>
        </div>
      </section>

      {/* ===================== TABS SECTION ====================== */}
      {/* <section className="w-full border-t border-gray-300 bg-[#f7f7f7] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-6 border-b border-gray-300 mb-10">
            {["description", "reviews"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 px-2 font-semibold text-lg transition-all 
                  ${
                    activeTab === tab
                      ? "text-red-600 border-b-2 border-red-600"
                      : "text-gray-600 hover:text-red-600"
                  }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div>
            {activeTab === "description" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-gray-700 leading-relaxed"
              >
                <h3 className="text-2xl font-semibold mb-4">
                  {data.productsDetails.descriptionTitle}
                </h3>
                <p>
                  {data.productsDetails.briefDescirption}
                </p>
              </motion.div>
            )}

            {activeTab === "reviews" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                <h3 className="text-2xl font-semibold mb-4">Client Reviews</h3>
                <div className="bg-white p-6 rounded-xl shadow border">
                  <div className="flex items-center gap-2">
                    <FaStar className="text-yellow-500" />
                    <FaStar className="text-yellow-500" />
                    <FaStar className="text-yellow-500" />
                    <FaStar className="text-yellow-500" />
                    <FaStar className="text-yellow-500" />
                  </div>
                  <p className="mt-4 text-gray-700">
                    “Exceptional glove box. Purity levels are unmatched!”
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section> */}
    </div>
  );
}
